import React from 'react';
import { browserUtils, theme, timeUtils } from '@sato/components';
import { capitalize } from 'lodash';
import {
  Datum,
  Point,
  PointProps,
  VictoryAxis,
  VictoryChart,
  VictoryLabel,
  VictoryLine,
  VictoryScatter,
} from 'victory';

import { chartTheme } from '@components/Charts/chartTheme';

const { isTouchDevice } = browserUtils;

interface Props {
  values: ChartCoordinate[];
  axisLabel?: string;
}

export interface ChartCoordinate {
  x: string;
  y: number;
}

export const LineChart = ({ values, axisLabel }: Props) => {
  const valuesForTicks = values.map((item) => item.y);
  const biggestTick = Math.ceil(Math.max(...valuesForTicks));
  const smallestTick = Math.floor(Math.min(...valuesForTicks));
  const tickValues = biggestTick - smallestTick;
  const ticks = new Array(tickValues / 0.5)
    .fill(0)
    .map((_, index) => smallestTick + index * 0.5);

  return (
    <VictoryChart height={260} theme={chartTheme}>
      <VictoryLine
        data={values}
        labels={({ datum, index }: { datum: Datum; index: `${number}` }) => {
          return index === '0' ? '' : datum.y;
        }}
        labelComponent={<VictoryLabel dy={-4} renderInPortal />}
      />

      <VictoryScatter
        size={isTouchDevice ? 5 : 3}
        dataComponent={<HideFirstPoint />}
        data={values}
      />

      <VictoryAxis
        dependentAxis
        tickValues={ticks}
        tickCount={5}
        label={axisLabel}
        axisLabelComponent={
          <VictoryLabel
            textAnchor="start"
            labelPlacement="vertical"
            // Positioning of the label from the center
            dy={-114}
            // Positioning of the label from the left
            dx={8}
            style={{ fontFamily: theme.fontFamily.header[0] }}
          />
        }
        tickLabelComponent={<VictoryLabel dx={-4} />}
      />

      <VictoryAxis
        crossAxis
        style={{ axis: { strokeWidth: 0 }, grid: { stroke: 'transparent' } }}
        tickLabelComponent={<VictoryLabel dy={4} />}
        tickFormat={(t: string, index: number) => {
          if (index === 0) return '';

          const result = capitalize(
            timeUtils.formatDate({ date: t, dateFormat: 'EEEEEE' })
          );

          return result;
        }}
      />
    </VictoryChart>
  );
};

const HideFirstPoint = ({ index, ...props }: PointProps) => {
  if (index === 0) return null;
  return <Point {...props} />;
};

export default LineChart;
